import type { TrainingScheduleOperationsItem } from "@/api/trainingSchedule";
import { scheduleStatusLabel } from "./scheduleFormat";

/**
 * Operations > Schedule 목록의 status 조회 축. 상태는 URL 이 소유한다(rules §17).
 *
 * 기본값은 **해제 제외**다. soft-cancel 된 스케줄은 행이 계속 남는다. 이 화면의 목적은 "지금
 * 실행 중인 반복" 관리다. 삭제된 행이 기본으로 쌓이면 활성 행이 묻힌다. 삭제 이력을 보려면 축의
 * 값을 바꾼다. 제외 조건은 별도 스위치가 아니라 이 축의 한 값이다(§17).
 *
 * 목록은 전체 응답이라 필터는 클라이언트에서 적용한다. 서버에 status 축이 없고, 행 수는 공용
 * workspace 의 스케줄 수라 상한이 있다.
 */

export const SCHEDULE_STATUS_PARAM = "status";

// "excludeCancelled" 가 기본(중립)이라 URL 에는 쓰지 않는다. 기본값 키를 지워서 정리된 URL 이
// canonical 이 되게 한다(Observability 와 같은 규약).
export const SCHEDULE_STATUS_FILTERS = [
  "excludeCancelled",
  "all",
  "active",
  "paused",
  "cancelled",
] as const;

export type ScheduleStatusFilter = (typeof SCHEDULE_STATUS_FILTERS)[number];

export const DEFAULT_SCHEDULE_STATUS_FILTER: ScheduleStatusFilter = "excludeCancelled";

export function normalizeScheduleStatusFilter(raw: string | null): ScheduleStatusFilter {
  return SCHEDULE_STATUS_FILTERS.includes(raw as ScheduleStatusFilter)
    ? (raw as ScheduleStatusFilter)
    : DEFAULT_SCHEDULE_STATUS_FILTER;
}

// URL 에 쓸 값. 기본값은 null(키 제거)이다.
export function serializeScheduleStatusFilter(filter: ScheduleStatusFilter): string | null {
  return filter === DEFAULT_SCHEDULE_STATUS_FILTER ? null : filter;
}

// 드롭다운 문구. 상태 값은 표시 어휘(scheduleStatusLabel)를 그대로 쓰고, 축 고유 값 두 개만
// 여기서 이름을 붙인다. 같은 상태가 칩과 칸에서 다른 말이 되지 않게 한다.
export function scheduleStatusFilterLabel(filter: ScheduleStatusFilter): string {
  switch (filter) {
    case "excludeCancelled":
      return "삭제 항목 제외";
    case "all":
      return "전체";
    default:
      return scheduleStatusLabel(filter);
  }
}

export function filterSchedulesByStatus(
  items: readonly TrainingScheduleOperationsItem[],
  filter: ScheduleStatusFilter,
): readonly TrainingScheduleOperationsItem[] {
  switch (filter) {
    case "all":
      return items;
    case "excludeCancelled":
      return items.filter((item) => item.status !== "cancelled");
    default:
      return items.filter((item) => item.status === filter);
  }
}
